/* eslint-disable react-hooks/refs -- false positive: prop forwarding, not ref.current reads */
import {KeyboardEvent, useCallback, useEffect, useRef} from 'react';

import {getSafely} from 'shared/web';

export type FocusZoneType = 'relative-dates' | 'calendar' | 'close-button';

export interface FocusTrapConfig {
  enabled: boolean;
  returnFocusOnClose: boolean;
  escapeKeyClosesPopper: boolean;
  tabCycleOrder: FocusZoneType[];
  initialFocusZone?: FocusZoneType;
}

export interface UseDatePickerFocusTrapProps {
  isOpen: boolean;
  onClose: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  popperRef: React.RefObject<HTMLDivElement | null>;
  hasRelativeDates: boolean;
  relativeDatesFocused: boolean;
  focusedRelativeDayIndex: number;
  calendarFocusedDate: Date | null;
  onRelativeDatesFocusChange: (focused: boolean, index?: number) => void;
  onCalendarFocusChange: (date: Date | null) => void;
  defaultCalendarDate?: Date;
  /** When false, opening the popper keeps focus on the input (e.g. click-to-type). */
  shouldInitializePopperFocus?: boolean;
  config?: Partial<FocusTrapConfig>;
}

export interface UseDatePickerFocusTrapReturn {
  handlePopperKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  focusTrapProps: {
    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
    tabIndex: -1;
    role: 'dialog';
    'aria-modal': boolean;
    'aria-label': string;
  };
  moveFocusToNextZone: () => void;
  moveFocusToPreviousZone: () => void;
  returnFocusToInput: (delay?: number) => void;
  currentFocusZone: FocusZoneType | null;
}

const DEFAULT_FOCUS_TRAP_CONFIG: FocusTrapConfig = {
  enabled: true,
  returnFocusOnClose: true,
  escapeKeyClosesPopper: true,
  tabCycleOrder: ['relative-dates', 'calendar', 'close-button'],
  initialFocusZone: 'relative-dates',
};

export function useDatePickerFocusTrap(
  props: UseDatePickerFocusTrapProps
): UseDatePickerFocusTrapReturn {
  const config = {...DEFAULT_FOCUS_TRAP_CONFIG, ...props.config};
  const currentFocusZoneRef = useRef<FocusZoneType | null>(null);
  const previousFocusElementRef = useRef<HTMLElement | null>(null);

  const availableZones = config.tabCycleOrder.filter((zone) => {
    if (zone === 'relative-dates' && !props.hasRelativeDates) {
      return false;
    }
    return true;
  });

  const getCurrentFocusZone = useCallback((): FocusZoneType | null => {
    if (props.relativeDatesFocused && props.hasRelativeDates) {
      return 'relative-dates';
    }
    if (props.calendarFocusedDate) {
      return 'calendar';
    }
    return currentFocusZoneRef.current;
  }, [props.relativeDatesFocused, props.hasRelativeDates, props.calendarFocusedDate]);

  const returnFocusToInput = useCallback(
    (delay = 0) => {
      if (props.inputRef.current && config.returnFocusOnClose) {
        if (delay > 0) {
          setTimeout(() => {
            props.inputRef.current?.focus();
          }, delay);
        } else {
          props.inputRef.current.focus();
        }
      }
    },
    [props.inputRef, config.returnFocusOnClose]
  );

  const {
    onRelativeDatesFocusChange,
    onCalendarFocusChange,
    defaultCalendarDate,
    hasRelativeDates,
    calendarFocusedDate,
    popperRef,
  } = props;
  const moveFocusToZone = useCallback(
    (targetZone: FocusZoneType) => {
      currentFocusZoneRef.current = targetZone;

      switch (targetZone) {
        case 'relative-dates':
          if (hasRelativeDates) {
            onRelativeDatesFocusChange(true, 0);
            onCalendarFocusChange(null);
          }
          break;
        case 'calendar':
          onRelativeDatesFocusChange(false, -1);
          if (!calendarFocusedDate) {
            const initialDate = defaultCalendarDate ?? new Date();
            onCalendarFocusChange(initialDate);
          }
          break;
        case 'close-button':
          onRelativeDatesFocusChange(false, -1);
          onCalendarFocusChange(null);
          // Focus close button - button will handle its own focus via tabIndex
          setTimeout(() => {
            const closeButton = popperRef.current?.querySelector(
              '[data-testid*="closeButton"]'
            ) as HTMLButtonElement;
            if (closeButton) {
              closeButton.focus();
            }
          }, 10);
          break;
      }
    },
    [
      hasRelativeDates,
      onRelativeDatesFocusChange,
      onCalendarFocusChange,
      calendarFocusedDate,
      defaultCalendarDate,
      popperRef,
    ]
  );

  const moveFocusToNextZone = useCallback(() => {
    const currentZone = getCurrentFocusZone();
    const currentIndex = currentZone ? availableZones.indexOf(currentZone) : -1;
    const nextIndex = currentIndex >= availableZones.length - 1 ? 0 : currentIndex + 1;
    const nextZone = getSafely(availableZones, nextIndex);

    if (nextZone) {
      moveFocusToZone(nextZone);
    }
  }, [getCurrentFocusZone, availableZones, moveFocusToZone]);

  const moveFocusToPreviousZone = useCallback(() => {
    const currentZone = getCurrentFocusZone();
    const currentIndex = currentZone ? availableZones.indexOf(currentZone) : -1;
    const prevIndex = currentIndex <= 0 ? availableZones.length - 1 : currentIndex - 1;
    const prevZone = getSafely(availableZones, prevIndex);

    if (prevZone) {
      moveFocusToZone(prevZone);
    }
  }, [getCurrentFocusZone, availableZones, moveFocusToZone]);

  const handleTabNavigation = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!config.enabled) {
        return;
      }

      if (event.key === 'Tab') {
        event.preventDefault();
        if (event.shiftKey) {
          moveFocusToPreviousZone();
        } else {
          moveFocusToNextZone();
        }
      }
    },
    [config.enabled, moveFocusToPreviousZone, moveFocusToNextZone]
  );

  const {onClose} = props;
  const handleEscapeKey = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape' && config.escapeKeyClosesPopper) {
        event.preventDefault();
        event.stopPropagation(); // Prevent ESC from bubbling to parent modals
        onClose();
      }
    },
    [config.escapeKeyClosesPopper, onClose]
  );

  const handlePopperKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!config.enabled) {
        return;
      }

      // Only handle Escape and Tab keys in focus trap
      // Let other keys (arrows, Enter, Space, etc.) pass through to existing handlers
      if (event.key === 'Escape') {
        handleEscapeKey(event);
        return;
      }

      if (event.key === 'Tab') {
        handleTabNavigation(event);
        return;
      }

      // For all other keys, let the default behavior continue
      // This allows arrow keys, Enter, Space, etc. to work in calendar and relative dates
    },
    [config.enabled, handleEscapeKey, handleTabNavigation]
  );

  // Store previous focus element when popper opens
  useEffect(
    function storePreviousFocusElement() {
      if (props.isOpen && !previousFocusElementRef.current) {
        previousFocusElementRef.current = document.activeElement as HTMLElement;
      } else if (!props.isOpen) {
        previousFocusElementRef.current = null;
        currentFocusZoneRef.current = null;
      }
    },
    [props.isOpen]
  );

  // Initialize focus zone when popper opens (after paint so chip/day refs exist)
  useEffect(
    function initializeFocusZone() {
      if (
        !props.isOpen ||
        !config.enabled ||
        currentFocusZoneRef.current ||
        !props.shouldInitializePopperFocus
      ) {
        return;
      }

      let initialZone = availableZones[0];
      if (config.initialFocusZone && availableZones.includes(config.initialFocusZone)) {
        initialZone = config.initialFocusZone;
      }

      if (!initialZone) {
        return;
      }

      const frameId = requestAnimationFrame(() => {
        moveFocusToZone(initialZone);
      });

      return () => cancelAnimationFrame(frameId);
    },
    [
      props.isOpen,
      props.shouldInitializePopperFocus,
      config.enabled,
      config.initialFocusZone,
      availableZones,
      moveFocusToZone,
    ]
  );

  const focusTrapProps = {
    onKeyDown: handlePopperKeyDown,
    tabIndex: -1 as const,
    role: 'dialog' as const,
    'aria-modal': props.isOpen && config.enabled,
    'aria-label': 'Date picker',
  };

  return {
    handlePopperKeyDown,
    focusTrapProps,
    moveFocusToNextZone,
    moveFocusToPreviousZone,
    returnFocusToInput,
    currentFocusZone: getCurrentFocusZone(),
  };
}
